/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Dominik Boeck <d.boeck@techdivision.com>
 * @author Marcus Biesel <m.biesel@techdivision.com>
 */

import { useEffect, useState } from 'react';
import { useQuery } from './useQuery';

/**
 * @param apiEndpointKey
 * @param signal
 * @param urlReplaceParameter
 * @param reactQueryOptions
 * @param axiosOptions
 * @returns {{lazyQuery: (value: unknown) => void}}
 */
export const useLazyQuery = (
  apiEndpointKey,
  signal = null,
  urlReplaceParameter = null,
  reactQueryOptions = {},
  axiosOptions = {},
) => {
  const [queryVariables, setQueryVariables] = useState(null);

  const queryDefaultOptions = {
    refetchOnWindowFocus: false,
    enabled: Boolean(queryVariables),
    retry: 0,
    staleTime: 0,
    cacheTime: 0,
  };

  const queryInfo = useQuery(
    apiEndpointKey,
    queryVariables,
    signal,
    urlReplaceParameter,
    { ...queryDefaultOptions, ...reactQueryOptions },
    axiosOptions,
  );

  useEffect(() => {
    if (queryInfo.status === 'success' || queryInfo.status === 'error') {
      setQueryVariables(null);
    }
  }, [queryInfo.status]);

  return {
    lazyQuery: setQueryVariables,
    ...queryInfo,
  };
};
